/**
 * @fileoverview One covered session's notes, as the organization reads and
 * writes them (docs/substitute-requests.md).
 *
 * A session a substitute covered has one lesson, and it is the organization's
 * side of the record: the substitute writes it from their link, an org
 * instructor who covered writes it in their portal, or the org writes it here
 * — "the organization can add lesson notes if they wish". The regular
 * instructor only ever reads it.
 *
 * Families hear about it only when the org sends it, after reading it here.
 * The send applies the rules every class summary follows — nobody whose
 * consent is withdrawn, under-13s through their parents only — and the email
 * says it was written by the substitute.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Tag } from "@/components/studio/primitives";
import { dayLabel } from "@/lib/orgReporting";
import { coverName } from "@/lib/substitutes";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

const EDITOR = { substitute: "the substitute", instructor: "the substitute", org_admin: "your organization" };

export default function SubstituteLessonDialog({ open, onOpenChange, classId, className, date, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await orgApi.get(`/org/classes/${classId}/sessions/substitute/lesson?date=${encodeURIComponent(date)}`);
      setData(res.data);
      setEditing(!res.data?.lesson);
      setTitle(res.data?.lesson?.title || className || "");
      setNotes(res.data?.lesson?.notes || "");
    } catch (err) {
      setData({ failed: true });
      toast.error(getOrgErrorMessage(err, "Couldn't load this session"));
    }
  }, [classId, date, className]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setConfirmSend(false);
    load();
  }, [open, load]);

  const lesson = data?.lesson || null;
  const sub = data?.substitute || null;

  const save = async () => {
    setSaving(true);
    try {
      const body = { date, title: title.trim() || className || "Class session", notes: notes.trim() };
      const res = lesson
        ? await orgApi.put(`/org/classes/${classId}/sessions/substitute/lesson`, body)
        : await orgApi.post(`/org/classes/${classId}/sessions/substitute/lesson`, body);
      setData(res.data);
      setEditing(false);
      toast.success("Notes saved");
      onChanged?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save the notes"));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await orgApi.post(`/org/class-lessons/${lesson.lesson_id}/send-email`);
      const sent = res.data?.sent ?? 0;
      const skipped = res.data?.skipped ?? 0;
      toast.success(`Sent to ${sent} ${sent === 1 ? "family" : "families"}${skipped ? ` · ${skipped} skipped` : ""}`);
      setConfirmSend(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send the notes"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="sub-lesson-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Substitute&rsquo;s notes</DialogTitle>
        </DialogHeader>
        <p className="font-sans text-[14px] text-[var(--text-secondary)]">
          {className} · {dayLabel(date, { year: true })}
          {sub ? ` · Covered by ${coverName(sub)}` : ""}
        </p>

        {!data ? (
          <p className="mt-4 font-sans text-[14px] text-[var(--text-tertiary)]">Loading…</p>
        ) : data.failed ? (
          <p className="mt-4 font-sans text-[14px] text-[var(--text-tertiary)]">Couldn&rsquo;t load this session.</p>
        ) : editing ? (
          <>
            {!lesson && (
              <p className="mt-3 font-sans text-[13px] text-[var(--text-tertiary)]">
                Nothing written yet. {sub?.kind === "roster" ? `${sub.name} can write these from their link, or ` : ""}
                You can write them here. They count as this session&rsquo;s lesson, taught by a substitute.
              </p>
            )}
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              aria-label="Title"
              maxLength={200}
              data-testid="sub-lesson-title"
              className={`${inputClass} mt-4`}
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was covered, how it went, anything the instructor should know"
              rows={8}
              aria-label="Notes"
              maxLength={20000}
              data-testid="sub-lesson-notes"
              className={`${inputClass} mt-3`}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button onPress={save} disabled={saving || !notes.trim()} data-testid="sub-lesson-save">
                {saving ? "Saving…" : "Save notes"}
              </Button>
              <button
                type="button"
                onClick={() => (lesson ? setEditing(false) : onOpenChange(false))}
                className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-sans text-[16px] font-medium text-[var(--text-primary)]">{lesson.title}</span>
              <Tag>Substitute</Tag>
              {lesson.email_sent && <Tag accent>Sent to families</Tag>}
            </div>
            <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]" data-testid="sub-lesson-byline">
              Written by {lesson.written_by_name || coverName(sub)}
              {lesson.last_edited_by && lesson.last_edited_by.type === "org_admin"
                ? ` · last edited by ${lesson.last_edited_by.name || EDITOR.org_admin}`
                : ""}
            </p>
            <div
              className="mt-3 max-h-[320px] overflow-y-auto whitespace-pre-wrap rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-overlay)] p-4 font-sans text-[14px] leading-relaxed text-[var(--text-primary)]"
              data-testid="sub-lesson-body"
            >
              {lesson.notes}
            </div>

            {confirmSend ? (
              <div className="mt-4 rounded-[12px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-4" data-testid="sub-lesson-confirm">
                <p className="font-sans text-[14px] text-[var(--text-primary)]">
                  Send these notes to the class&rsquo;s families?
                </p>
                <p className="mt-1 font-sans text-[13px] text-[var(--text-secondary)]">
                  The same rules as every class summary apply: nobody whose consent is withdrawn, and students under 13
                  through their parents only. The email says it was written by {lesson.written_by_name || "the substitute"}.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button onPress={send} disabled={sending} data-testid="sub-lesson-send-confirm">
                    {sending ? "Sending…" : "Send to families"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setConfirmSend(false)}
                    className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
                  >
                    Not now
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {!lesson.email_sent && (
                  <Button onPress={() => setConfirmSend(true)} data-testid="sub-lesson-send">
                    Send to families
                  </Button>
                )}
                <Button variant="ghost" onPress={() => setEditing(true)} data-testid="sub-lesson-edit">
                  Edit notes
                </Button>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
                >
                  Close
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
